<template>
  <div id="candidate-years-experience">
    <vx-card title="Procesos por avance">
      <!-- LOADING -->
      <div
        v-if="uiState === 'loading'"
        class="gw-empty-state"
        style="height:350px;"
      >
        <div class="gw-spinner" aria-label="Cargando"></div>
        <div class="gw-empty-title">Cargando información…</div>
        <div class="gw-empty-subtitle">Un momento por favor</div>
      </div>

      <!-- READY (CHART) -->
      <apexcharts
        v-else-if="uiState === 'ready' && renderGraphic"
        type="bar"
        height="350"
        :options="chartOptions"
        :series="series"
      />

      <!-- EMPTY / ERROR -->
      <div
        v-else
        class="gw-empty-state"
        style="height:350px;"
      >
        <img
          class="gw-empty-img"
          :src="emptyStateImage"
          :alt="emptyStateAlt"
        />
        <div class="gw-empty-title">{{ emptyStateTitle }}</div>
        <div class="gw-empty-subtitle">{{ emptyStateSubtitle }}</div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import { publicAssetUrl } from '@/utils/publicAssets'
import { eventBus } from "./../../main";
import VueApexCharts from "vue-apexcharts";
import api from "../../mixins/api";
import { mapGetters } from "vuex";

export default {
  props: ["startDate", "endDate", "cityId"],
  mixins: [api],

  data() {
    return {
      // UI states: 'loading' | 'ready' | 'empty' | 'error'
      uiState: "loading",
      renderGraphic: false,

      requestKey: null,
      consultHandler: null,

      // Empty/Error illustrations (S3)
      IMG_EMPTY:
        publicAssetUrl('Assets/illustrations/no_data_found.png'),
      IMG_ERROR:
        publicAssetUrl('Assets/illustrations/error.png'),

      series: [
        {
          name: "% de Procesos",
          data: [],
        },
      ],

      chartOptions: {
        chart: { height: 350, type: "bar" },
        plotOptions: {
          bar: {
            columnWidth: "45%",
            distributed: true,
            dataLabels: { position: "top" },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val + "%";
          },
          offsetY: -20,
          style: {
            fontSize: "12px",
            colors: ["#304758"],
          },
        },
        legend: { show: false },
        colors: ["#5F0091"],
        xaxis: {
          categories: ["Finalizados", "En Progreso", "Preliminar"],
          labels: { style: { fontSize: "12px" } },
        },
        yaxis: {
          labels: {
            formatter: function (value) {
              return value + "%";
            },
          },
        },
      },
    };
  },

  components: {
    apexcharts: VueApexCharts,
  },

  computed: {
    ...mapGetters(["userInfo"]),

    emptyStateImage() {
      return this.uiState === "error" ? this.IMG_ERROR : this.IMG_EMPTY;
    },

    emptyStateAlt() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "No se encontraron resultados";
    },

    emptyStateTitle() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "Sin datos para el filtro seleccionado";
    },

    emptyStateSubtitle() {
      return this.uiState === "error"
        ? "Inténtalo de nuevo o ajusta los filtros."
        : "Prueba con otro rango de fechas o ciudad.";
    },
  },

  methods: {
    getUrl() {
      var path =
        "/metrics/projects-by-advance?client_id=" +
        this.userInfo.user.client_id;

      var url = "";

      if (this.startDate != null && this.endDate != null) {
        var params =
          "&start_date=" + this.startDate + "&end_date=" + this.endDate;
        url = this.url() + path + params;
      } else {
        url = this.url() + path;
      }

      if (this.cityId != null) url += "&city_id=" + this.cityId;

      return url;
    },

    resetChart() {
      this.renderGraphic = false;
      this.series = [{ name: "% de Procesos", data: [] }];
    },

    setLoading(key) {
      this.requestKey = key;
      this.uiState = "loading";
      this.resetChart();
    },

    setEmpty() {
      this.uiState = "empty";
      this.resetChart();
    },

    setError() {
      this.uiState = "error";
      this.resetChart();
    },

    setReady(seriesData) {
      // Remount para evitar glitches
      this.renderGraphic = false;
      this.series = [{ name: "% de Procesos", data: seriesData }];
      this.renderGraphic = true;
      this.uiState = "ready";
    },

    async initializeData(origin) {
      if (!origin) origin = "unknown";

      var key =
        (this.startDate || "") +
        "|" +
        (this.endDate || "") +
        "|" +
        (this.cityId || "");

      this.setLoading(key);

      console.log("[projects-by-advance] initializeData:", {
        origin: origin,
        startDate: this.startDate,
        endDate: this.endDate,
        cityId: this.cityId,
        url: this.getUrl(),
      });

      try {
        var request = await this.requestGetWithoutContent(this.getUrl(), {});

        // Evitar race conditions
        if (this.requestKey !== key) {
          console.log("[projects-by-advance] stale response ignored");
          return;
        }

        if (!request || request.status !== 200) {
          console.warn(
            "[projects-by-advance] request not ok:",
            request && request.status
          );
          this.setError();
          return;
        }

        var data = request.data;

        // Esperamos array con 1 row
        if (!Array.isArray(data) || data.length === 0 || !data[0]) {
          console.warn("[projects-by-advance] Empty data");
          this.setEmpty();
          return;
        }

        this.applyRow(data[0]);
      } catch (e) {
        if (this.requestKey !== key) return;
        console.warn("[projects-by-advance] request exception:", e);
        this.setError();
      }
    },

    applyRow(row) {
      var values = Object.values(row);

      // Esperado: [TotalProcess, finished, preliminary, inprocess] (según tu query)
      var total = parseFloat(values[0]);

      // Si total inválido o 0 -> empty
      if (!total || isNaN(total) || total <= 0) {
        console.warn("[projects-by-advance] total invalid/zero");
        this.setEmpty();
        return;
      }

      var seriesData = [];
      for (var i = 1; i <= 3; i++) {
        var val = parseFloat(values[i]);
        val = !isNaN(val) ? val : 0;
        seriesData.push(((val * 100) / total).toFixed(2));
      }

      // Si todo queda 0, no mostramos chart
      var sum = 0;
      for (var j = 0; j < seriesData.length; j++) sum += parseFloat(seriesData[j]);
      if (!sum || isNaN(sum) || sum <= 0) {
        console.warn("[projects-by-advance] series all zero");
        this.setEmpty();
        return;
      }

      console.log("[projects-by-advance] series final:", seriesData);
      this.setReady(seriesData);
    },
  },

  mounted() {
    this.initializeData("mounted");

    this.consultHandler = () => this.initializeData("eventBus:consultData");
    eventBus.$on("consultData", this.consultHandler);
  },

  beforeDestroy() {
    if (this.consultHandler) {
      eventBus.$off("consultData", this.consultHandler);
    }
  },
};
</script>

<style scoped>
.gw-empty-state{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 10px;
  text-align:center;
  padding: 16px;
}

.gw-empty-img{
  max-width: 260px;
  width: 100%;
  height: auto;
}

.gw-empty-title{
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}

.gw-empty-subtitle{
  font-size: 12px;
  color: #7a7a7a;
}

.gw-spinner{
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95,0,145,0.18);
  border-top-color: rgba(95,0,145,0.9);
  animation: gwspin 0.9s linear infinite;
}

@keyframes gwspin{
  to { transform: rotate(360deg); }
}
</style>
